/**
 * Jurisdiction Edit (SystemAdmin only).
 *
 * Code + Name are read-only (identity columns). Everything else in the green sections
 * is editable: General, Capabilities, Public Contact, Record Request, Data Retention.
 * Browser-automation columns (PortalUrl, PortalCredentialKey, PortalTechnology,
 * BrowserTimeoutSeconds, LookupMethod) are not part of the API response shape so they
 * never appear here.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Map, Loader2, ArrowLeft, Save, CheckCircle, AlertCircle, ChevronDown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Navigation from '../../components/Navigation';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import { getErrorMessage } from '../../services/apiService';
import { maskPhoneInput } from '../../utils/validation';
import JurisdictionReportSections from '../../components/admin/JurisdictionReportSections';
import {
  getJurisdiction,
  updateJurisdiction,
  JurisdictionAdminResponse,
  UpdateJurisdictionRequest,
} from '../../services/jurisdictionsAdminService';

const Section: React.FC<{ title: string; defaultOpen?: boolean; children: React.ReactNode }> = ({ title, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-2xl border border-black/[0.08] shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between px-6 py-4 text-left bg-black/[0.02] hover:bg-black/[0.04] transition-colors ${open ? 'border-b border-black/[0.08]' : ''}`}
      >
        <h2 className="text-sm font-semibold uppercase tracking-wider text-[#475569]">{title}</h2>
        <ChevronDown className={`w-4 h-4 text-[#94a3b8] transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && <div className="px-6 py-5">{children}</div>}
    </div>
  );
};

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="block text-xs uppercase tracking-wider text-[#94a3b8] mb-1">{children}</label>
);

const TextInput: React.FC<{ value: string; onChange: (v: string) => void; placeholder?: string }> = ({ value, onChange, placeholder }) => (
  <input
    type="text"
    value={value}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm focus:outline-none focus:border-[#c41e3a]/50"
  />
);

const NumberInput: React.FC<{ value: number; onChange: (v: number) => void; min?: number }> = ({ value, onChange, min = 0 }) => (
  <input
    type="number"
    min={min}
    value={value}
    onChange={(e) => onChange(Math.max(min, parseInt(e.target.value, 10) || min))}
    className="w-32 px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm focus:outline-none focus:border-[#c41e3a]/50"
  />
);

const Toggle: React.FC<{ label: string; value: boolean; onChange: (v: boolean) => void }> = ({ label, value, onChange }) => (
  <label className="inline-flex items-center gap-2 cursor-pointer select-none">
    <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#c41e3a]" />
    <span className="text-sm text-[#0f172a]">{label}</span>
  </label>
);

// Retention: empty means "use the global default". Only a SystemAdmin can set a number.
const RetentionInput: React.FC<{ value: number | null; onChange: (v: number | null) => void; disabled?: boolean }> = ({ value, onChange, disabled }) => (
  <input
    type="number"
    min={1}
    value={value ?? ''}
    placeholder="Global default"
    disabled={disabled}
    onChange={(e) => {
      const n = parseInt(e.target.value, 10);
      onChange(Number.isNaN(n) ? null : Math.max(1, n));
    }}
    className="w-40 px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm focus:outline-none focus:border-[#c41e3a]/50 disabled:bg-[#f1f5f9] disabled:text-[#64748b]"
  />
);

const TextArea: React.FC<{ value: string; onChange: (v: string) => void; rows?: number }> = ({ value, onChange, rows = 4 }) => (
  <textarea
    value={value}
    rows={rows}
    onChange={(e) => onChange(e.target.value)}
    className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm focus:outline-none focus:border-[#c41e3a]/50 resize-y"
  />
);

const ReadOnly: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <Label>{label}</Label>
    <div className="text-sm text-[#0f172a]">{value ?? <span className="text-[#94a3b8]">—</span>}</div>
  </div>
);

// Type is a 4-value de-facto enumeration (plain varchar on the column, but no other
// values are valid downstream). Surface as a dropdown so admins can't accidentally
// type something off-list.
const JURISDICTION_TYPES = ['State', 'Territory', 'FreelyAssociatedState', 'DistrictOfColumbia'] as const;

// FormState carries every editable field as a non-null primitive so the form is easy
// to drive. We only send to the API the keys that differ from the loaded data, which
// keeps the PATCH calls clean and lets the server-side null-vs-empty handling work.
interface FormState {
  displayOrder: number;
  type: string;
  isActive: boolean;
  nmvtisCovered: boolean;
  dmvConnected: boolean;
  dmvComingSoon: boolean;
  mvrEnabledByDefault: boolean;
  supportsTitle: boolean;
  supportsRegistration: boolean;
  hasPublicPortal: boolean;
  contactMailingAddress: string;
  contactPhone: string;
  contactEmail: string;
  contactWebsiteUrl: string;
  recordRequestUrl: string;
  recordRequestFormUrl: string;
  recordRequestInstructions: string;
  recordRequestFee: string;
  dataRetentionDays: number | null;
}

const toFormState = (j: JurisdictionAdminResponse): FormState => ({
  displayOrder: j.displayOrder,
  type: j.type,
  isActive: j.isActive,
  nmvtisCovered: j.nmvtisCovered,
  dmvConnected: j.dmvConnected,
  dmvComingSoon: j.dmvComingSoon ?? false,
  mvrEnabledByDefault: j.mvrEnabledByDefault ?? true,
  supportsTitle: j.supportsTitle,
  supportsRegistration: j.supportsRegistration,
  hasPublicPortal: j.hasPublicPortal,
  contactMailingAddress: j.contactMailingAddress ?? '',
  contactPhone: j.contactPhone ?? '',
  contactEmail: j.contactEmail ?? '',
  contactWebsiteUrl: j.contactWebsiteUrl ?? '',
  recordRequestUrl: j.recordRequestUrl ?? '',
  recordRequestFormUrl: j.recordRequestFormUrl ?? '',
  recordRequestInstructions: j.recordRequestInstructions ?? '',
  recordRequestFee: j.recordRequestFee ?? '',
  dataRetentionDays: j.dataRetentionDays,
});

// Diff form vs server, returning only changed keys with the correct type
// (null when a previously-non-empty string was cleared).
const diff = (orig: FormState, cur: FormState): UpdateJurisdictionRequest => {
  const out: UpdateJurisdictionRequest = {};
  if (cur.displayOrder !== orig.displayOrder) out.displayOrder = cur.displayOrder;
  if (cur.type !== orig.type) out.type = cur.type;
  if (cur.isActive !== orig.isActive) out.isActive = cur.isActive;
  if (cur.nmvtisCovered !== orig.nmvtisCovered) out.nmvtisCovered = cur.nmvtisCovered;
  if (cur.dmvConnected !== orig.dmvConnected) out.dmvConnected = cur.dmvConnected;
  if (cur.dmvComingSoon !== orig.dmvComingSoon) out.dmvComingSoon = cur.dmvComingSoon;
  if (cur.mvrEnabledByDefault !== orig.mvrEnabledByDefault) out.mvrEnabledByDefault = cur.mvrEnabledByDefault;
  if (cur.supportsTitle !== orig.supportsTitle) out.supportsTitle = cur.supportsTitle;
  if (cur.supportsRegistration !== orig.supportsRegistration) out.supportsRegistration = cur.supportsRegistration;
  if (cur.hasPublicPortal !== orig.hasPublicPortal) out.hasPublicPortal = cur.hasPublicPortal;
  if (cur.contactMailingAddress !== orig.contactMailingAddress) out.contactMailingAddress = cur.contactMailingAddress || null;
  if (cur.contactPhone !== orig.contactPhone) out.contactPhone = cur.contactPhone || null;
  if (cur.contactEmail !== orig.contactEmail) out.contactEmail = cur.contactEmail || null;
  if (cur.contactWebsiteUrl !== orig.contactWebsiteUrl) out.contactWebsiteUrl = cur.contactWebsiteUrl || null;
  if (cur.recordRequestUrl !== orig.recordRequestUrl) out.recordRequestUrl = cur.recordRequestUrl || null;
  if (cur.recordRequestFormUrl !== orig.recordRequestFormUrl) out.recordRequestFormUrl = cur.recordRequestFormUrl || null;
  if (cur.recordRequestInstructions !== orig.recordRequestInstructions) out.recordRequestInstructions = cur.recordRequestInstructions || null;
  if (cur.recordRequestFee !== orig.recordRequestFee) out.recordRequestFee = cur.recordRequestFee || null;
  if (cur.dataRetentionDays !== orig.dataRetentionDays) out.dataRetentionDays = cur.dataRetentionDays ?? 0;
  return out;
};

const JurisdictionEdit: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isSystemAdmin = user?.isSystemAdmin === true;

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate('/login');
    else if (!isSystemAdmin) navigate('/dashboard');
  }, [user, authLoading, navigate, isSystemAdmin]);

  const [data, setData] = useState<JurisdictionAdminResponse | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const load = useCallback(async () => {
    if (!code) return;
    try {
      setIsLoading(true);
      setError('');
      const j = await getJurisdiction(code);
      setData(j);
      setForm(toFormState(j));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [code]);

  useEffect(() => { if (isSystemAdmin) load(); }, [isSystemAdmin, load]);

  const handleSave = async () => {
    if (!code || !data || !form) return;

    const orig = toFormState(data);
    const patch = diff(orig, form);
    if (Object.keys(patch).length === 0) return;

    try {
      setSaving(true);
      setError('');
      const updated = await updateJurisdiction(code, patch);
      setData(updated);
      setForm(toFormState(updated));
      setSuccess('Changes saved.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || isLoading || !data || !form) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0]">
        <Loader2 className="w-8 h-8 animate-spin text-[#c41e3a]" />
      </div>
    );
  }

  const orig = toFormState(data);
  const dirty = Object.keys(diff(orig, form)).length > 0;

  const setF = (k: keyof FormState, v: FormState[keyof FormState]) => setForm({ ...form, [k]: v } as FormState);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] text-[#0f172a]">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-radial from-[#c41e3a]/[0.03] to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-radial from-[#c41e3a]/[0.04] to-transparent rounded-full blur-3xl"></div>
      </div>

      <Navigation onMenuClick={() => setIsSidebarOpen(true)} />
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <main className="flex-1 lg:ml-[220px] pt-28 md:pt-32 px-4 lg:px-8 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <button onClick={() => navigate('/admin/jurisdictions')} className="flex items-center gap-2 text-sm text-[#475569] hover:text-[#c41e3a] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Jurisdictions
          </button>

          <div className="flex items-center gap-3">
            <Map className="w-7 h-7 text-[#c41e3a]" />
            <div>
              <h1 className="text-2xl font-bold text-[#0f172a]">{data.name}</h1>
              <p className="text-sm text-[#64748b]">Code <span className="font-mono">{data.code}</span> · read-only identity</p>
            </div>
          </div>

          {error && (
            <div className="p-4 rounded-xl border flex items-start space-x-3 bg-red-50 border-red-200">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
          {success && (
            <div className="p-4 rounded-xl border flex items-start space-x-3 bg-emerald-50 border-emerald-200">
              <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-800">{success}</p>
            </div>
          )}

          <Section title="General">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <ReadOnly label="Code" value={<span className="font-mono">{data.code}</span>} />
              <ReadOnly label="Name" value={data.name} />
              <div>
                <Label>Type</Label>
                <select
                  value={form.type}
                  onChange={(e) => setF('type', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm focus:outline-none focus:border-[#c41e3a]/50"
                >
                  {/* If the row was already loaded with a non-canonical Type (shouldn't
                      happen but defensive), include it so the dropdown doesn't blank out. */}
                  {!JURISDICTION_TYPES.includes(form.type as typeof JURISDICTION_TYPES[number]) && form.type && (
                    <option value={form.type}>{form.type} (current)</option>
                  )}
                  {JURISDICTION_TYPES.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Display Order</Label>
                <NumberInput value={form.displayOrder} onChange={(v) => setF('displayOrder', v)} />
              </div>
              <div className="flex items-end">
                <Toggle label="Active" value={form.isActive} onChange={(v) => setF('isActive', v)} />
              </div>
            </div>
          </Section>

          <Section title="Capabilities">
            {/* NMVTIS is a national title-history service, not a per-state DMV capability,
                so it is intentionally not a jurisdiction capability here. */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Toggle label="DMV Connected" value={form.dmvConnected} onChange={(v) => setF('dmvConnected', v)} />
              {/* Home page DMV map only: marks a connection we are building as "Coming Soon".
                  Grants no lookups, and a connected state shows as connected regardless. */}
              <Toggle label="DMV Coming Soon (home map)" value={form.dmvComingSoon} onChange={(v) => setF('dmvComingSoon', v)} />
              {/* Separate from DMV Connected on purpose: that one says we built the
                  integration, this one says customers may use it. South Carolina and Florida
                  are connected but withdrawn — broken on the state's side and an unreliable
                  CAPTCHA respectively. Turning this off hides the state from every client that
                  has no explicit setting of its own, including clients created later. System
                  admins still see it, which is how you tell whether it is working again. */}
              <Toggle label="Available to Clients (MVR)" value={form.mvrEnabledByDefault} onChange={(v) => setF('mvrEnabledByDefault', v)} />
              <Toggle label="Has Public Portal" value={form.hasPublicPortal} onChange={(v) => setF('hasPublicPortal', v)} />
              <Toggle label="Supports Title" value={form.supportsTitle} onChange={(v) => setF('supportsTitle', v)} />
              <Toggle label="Supports Registration" value={form.supportsRegistration} onChange={(v) => setF('supportsRegistration', v)} />
            </div>
          </Section>

          <Section title="Public Contact">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Mailing Address</Label>
                <TextInput value={form.contactMailingAddress} onChange={(v) => setF('contactMailingAddress', v)} />
              </div>
              <div>
                <Label>Phone</Label>
                <TextInput value={form.contactPhone} placeholder="(555) 555-0100" onChange={(v) => setF('contactPhone', maskPhoneInput(v))} />
              </div>
              <div>
                <Label>Email</Label>
                <TextInput value={form.contactEmail} onChange={(v) => setF('contactEmail', v)} />
              </div>
              <div>
                <Label>Website URL</Label>
                <TextInput value={form.contactWebsiteUrl} onChange={(v) => setF('contactWebsiteUrl', v)} />
              </div>
            </div>
          </Section>

          <Section title="Record Request">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Request URL</Label>
                <TextInput value={form.recordRequestUrl} onChange={(v) => setF('recordRequestUrl', v)} />
              </div>
              <div>
                <Label>Form URL</Label>
                <TextInput value={form.recordRequestFormUrl} onChange={(v) => setF('recordRequestFormUrl', v)} />
              </div>
              <div>
                <Label>Fee</Label>
                <TextInput value={form.recordRequestFee} onChange={(v) => setF('recordRequestFee', v)} placeholder="$10 per record" />
              </div>
              <div className="md:col-span-2">
                <Label>Instructions</Label>
                <TextArea value={form.recordRequestInstructions} onChange={(v) => setF('recordRequestInstructions', v)} />
              </div>
            </div>
          </Section>

          <Section title="Data Retention">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Retention (days)</Label>
                <RetentionInput value={form.dataRetentionDays} onChange={(v) => setF('dataRetentionDays', v)} />
                <p className="text-xs text-[#64748b] mt-2 leading-relaxed">
                  Calendar days a state-DMV record for {data.name} is kept before the nightly
                  purge deletes it. Empty means the global default applies. The clock starts at midnight at the start of the day a
                  record was created, in the <strong>owning client's</strong> timezone.
                </p>
              </div>
              <ReadOnly label="Maximum Retention (reserved)" value={data.dataRetentionMaxDays ?? 'Not enforced'} />
            </div>
          </Section>

          <JurisdictionReportSections code={data.code} />

          {/* Sticky save bar — only shown when the jurisdiction form itself has unsaved
              edits, so it doesn't compete with each section's own save (e.g. the Report
              Template panel's "Save template"). */}
          {dirty && (
            <div className="sticky bottom-4 bg-white rounded-2xl border border-black/[0.08] p-4 shadow-lg flex items-center justify-between gap-3">
              <div className="text-xs text-[#64748b]">Unsaved jurisdiction changes</div>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-[#c41e3a] to-[#a01830] hover:from-[#a01830] hover:to-[#8a1428] disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium transition-all shadow-lg shadow-[#c41e3a]/20"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save jurisdiction
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default JurisdictionEdit;
